// The browser tab's title: where you are, then the product. "Invoices –
// Sendinvo", "New quote – Sendinvo", and a document open in its drawer over
// its list, "INV-0021 – Invoices – Sendinvo". In a demo the product reads
// "Sendinvo demo", so a demo tab is never mistaken for a real account's.
//
// This module owns the words and the order. render() in app.js names the
// route as it dispatches (`showRouteTitle`), and the three drawers a route
// opens (an invoice, a quote, a job) name what they show once it has loaded
// (`showDocumentTitle`), and put the route's back when they close
// (`showRouteTitle` again from their unmount). Nothing else writes
// document.title.
//
// The parts are joined with a spaced en dash, the separator COPY.md allows in
// a tab title and nowhere else. The marketing landing at "/" keeps the title
// its HTML was served with (its SEO title, held by check:marketing-seo), read
// once as this module loads, before anything has changed it.
import { isDemoPage, parseRouteState } from "./navigation.js";
import { getScheduleIdFromPath } from "./schedule-format.js";
import { getQuoteIdFromPath } from "./quote-format.js";

export const TITLE_SEPARATOR = " – ";
export const PRODUCT_TITLE = "Sendinvo";
export const DEMO_PRODUCT_TITLE = "Sendinvo demo";

const LANDING_TITLE = typeof document !== "undefined" ? document.title : "";

// A section's name, as its sidebar item or Company row says it.
const SECTIONS = {
  "/schedule": "Schedule",
  "/invoices": "Invoices",
  "/jobs": "Jobs",
  "/quotes": "Quotes",
  "/contacts": "Clients",
  "/schedules": "Recurring invoices",
  "/team": "Team",
  "/profile": "Profile",
  "/company/company-details": "Company details",
  "/company/subscription": "Subscription",
  "/company/payments": "Payments",
  "/company/invoicing": "Invoices and quotes",
  "/company/reminders": "Reminders",
  "/company/accounting": "Accounting",
  "/company/items": "Items",
  "/company/visit-hours": "Visit hours",
  "/company/visit-reminders": "Visit reminders",
  "/company/reviews": "Reviews",
  "/company/import": "Import"
};

// Routes that are a page of their own, named for what you do there.
const PAGES = {
  "/login": "Log in",
  "/register": "Create your account",
  "/verify": "Check your email",
  "/forgot-password": "Reset password",
  "/forgot-password/check-email": "Reset password",
  "/forgot-password/set-password": "Set your password",
  "/forgot-password/done": "Reset password",
  "/privacy": "Privacy Policy",
  "/terms": "Terms of Service",
  "/invoices/new": "New invoice",
  "/quotes/new": "New quote",
  "/jobs/new": "New job",
  "/schedules/new": "New recurring invoice",
  "/contacts/add": "New client",
  "/team/activity": "Team activity",
  "/company": "Company details",
  "/company/email-template": "Invoice email",
  "/company/quote-email-template": "Quote email",
  "/company/credit-note-email-template": "Credit note email"
};

// What a document is called in its title: its number, else what it is.
const DOCUMENT_KINDS = {
  invoice: { section: "Invoices", number: "invoiceNumber", draft: "Draft invoice" },
  quote: { section: "Quotes", number: "quoteNumber", draft: "Draft quote" },
  job: { section: "Jobs", number: "jobNumber", draft: "Job" }
};

/** The title for these parts, the product last. */
export function titleFrom(parts, { demo = false } = {}) {
  const named = (Array.isArray(parts) ? parts : []).map((p) => String(p ?? "").trim()).filter(Boolean);
  return [...named, demo ? DEMO_PRODUCT_TITLE : PRODUCT_TITLE].join(TITLE_SEPARATOR);
}

/**
 * The parts a route's title is made of, before the product: ["Invoices"],
 * ["New job"]. A route that opens a document in its drawer names its section
 * until the document has loaded and says what it is. `null` for "/", the
 * landing, which keeps the title it was served with, and for a route the app
 * doesn't have (render() sends that one home, which names itself).
 */
export function routeTitleParts(path) {
  const p = String(path || "/").split("?")[0].replace(/\/+$/, "") || "/";
  if (p === "/") return null;
  if (PAGES[p]) return [PAGES[p]];
  if (SECTIONS[p]) return [SECTIONS[p]];
  if (/^\/profile\/[^/]+$/.test(p)) return [SECTIONS["/profile"]];
  if (/^\/company\/reminders\/[a-z-]+$/.test(p)) return ["Reminder email"];
  if (/^\/invoices\/[^/]+$/.test(p)) return [SECTIONS["/invoices"]];
  if (getQuoteIdFromPath(p)) return [SECTIONS["/quotes"]];
  if (/^\/jobs\/[^/]+$/.test(p)) return [SECTIONS["/jobs"]];
  if (/^\/schedules\/[^/]+\/edit$/.test(p)) return ["Edit recurring invoice"];
  if (getScheduleIdFromPath(p)) return ["Recurring invoice"];
  if (/^\/invite\/[^/]+$/.test(p)) return ["Accept invite"];
  // The demo's client-facing pages: what the client would see.
  if (/^\/pay\/[^/]+$/.test(p)) return ["Pay invoice"];
  if (/^\/q\/[^/]+$/.test(p)) return ["Quote"];
  return null;
}

/** A document in its drawer: ["INV-0021", "Invoices"], ["Draft quote", "Quotes"], a job by its name. */
export function documentTitleParts(kind, doc) {
  const k = DOCUMENT_KINDS[kind];
  if (!k) return null;
  const number = String(doc?.[k.number] ?? "").trim();
  const name = kind === "job" ? String(doc?.name ?? "").trim() : "";
  return [name || number || k.draft, k.section];
}

/** The whole title for a route: its parts and the product, or the landing's own. */
export function routeTitle(path, { demo = false, landing = LANDING_TITLE } = {}) {
  const parts = routeTitleParts(path);
  if (!parts) return landing || titleFrom([], { demo });
  return titleFrom(parts, { demo });
}

function write(title) {
  if (typeof document !== "undefined" && title && document.title !== title) document.title = title;
}

/** Name the route showing now. render() calls it; so does a drawer as it closes. */
export function showRouteTitle(path = parseRouteState().path) {
  write(routeTitle(path, { demo: isDemoPage() }));
}

/** Name a page by its own words, where the route can't (a Field person's home). */
export function showPageTitle(parts) {
  write(titleFrom(parts, { demo: isDemoPage() }));
}

/** Name the document a drawer (or a draft's compose screen) is showing. */
export function showDocumentTitle(kind, doc) {
  const parts = documentTitleParts(kind, doc);
  if (parts) showPageTitle(parts);
}
